import React, { useState, useMemo } from 'react';
import styles from '../../styles/UserPage.module.css';
import {
  MODEL_OPTIONS,
  modelListFor,
  pickDefaultModel,
  providerBase,
  unlistedOption,
} from '../../utils/modelConstants';
import { deviceModelsByKey } from '../../utils/modelLists';
import { DISABLED_PROVIDER } from '../../utils/userPageHelpers';
import ModelReliabilityPanel from './ModelReliabilityPanel';
import ProfileSwitcher from './ProfileSwitcher';

// Gray styling for the "Select ..." placeholder options, matching the left
// panel (LLMSettingsSection) so the empty state reads the same in both
// places: only the placeholder is gray — never the real model options.
const PLACEHOLDER_STYLE = { color: '#999' };


// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

/**
 * Chip list for user-added custom models with an × remove button each.
 * Only used for non-local providers — local models come from the device.
 */
function CustomModelChips({ models, onRemove }) {
  if (!models || models.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
      {models.map(model => (
        <span
          key={model}
          style={{
            padding: '0.2rem 0.45rem',
            background: 'var(--input-background)',
            border: '1px solid var(--input-border)',
            borderRadius: '4px',
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
          }}
        >
          {model}
          <button
            onClick={() => onRemove(model)}
            title={`Remove ${model}`}
            style={{
              background: 'none',
              border: 'none',
              color: '#e5393f',
              cursor: 'pointer',
              padding: 0,
              fontSize: '1rem',
              lineHeight: 1,
              fontWeight: 'bold',
            }}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

/**
 * Inline add-model form for custom models not already in the catalog.
 * Only used for non-local providers.
 */
function AddModelForm({
  title,
  value,
  onChange,
  onAdd,
  onCancel,
  isAdding,
}) {
  return (
    <div style={{
      background: 'var(--input-background)',
      border: '1px solid var(--input-border)',
      borderRadius: '6px',
      padding: '0.75rem',
      marginTop: '0.5rem',
    }}>
      <p style={{
        fontSize: '0.85rem',
        fontWeight: '600',
        marginBottom: '0.5rem',
        color: 'var(--text-color)',
      }}>
        {title}
      </p>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={styles.input}
        placeholder="Enter model identifier (e.g. gpt-4.1-mini)"
        disabled={isAdding}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void onAdd();
          if (e.key === 'Escape') onCancel();
        }}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        style={{ marginBottom: '0.5rem' }}
      />
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={() => void onAdd()}
          className={styles.button}
          disabled={isAdding || !value.trim()}
          style={{
            opacity: isAdding || !value.trim() ? 0.6 : 1,
            cursor: isAdding || !value.trim() ? 'not-allowed' : 'pointer',
            backgroundColor: value.trim() ? 'var(--button-background)' : undefined,
            color: value.trim() ? 'var(--button-text)' : undefined,
            border: value.trim() ? '1px solid var(--button-background)' : undefined,
          }}
        >
          {isAdding ? 'Adding...' : 'Add Model'}
        </button>
        <button
          onClick={onCancel}
          className={styles.button}
          disabled={isAdding}
          style={{ backgroundColor: '#e5393f', color: 'white', border: 'none' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * A disabled select indicating the local device is offline or unreachable.
 */
function OfflineDeviceSelect() {
  return (
    <select
      disabled
      style={{
        opacity: 0.5,
        cursor: 'not-allowed',
        fontStyle: 'italic',
      }}
    >
      <option>Device offline — no models available</option>
    </select>
  );
}

/**
 * The offline-aware <option> list for the user's saved provider configs.
 * A local config whose device reports no installed models (once devices
 * have loaded) renders disabled with an " (offline)" suffix.  Shared by the
 * Main/Summarizer ProviderSelect and the per-agent provider dropdown so the
 * offline detection lives in exactly one place.
 */
function ProviderConfigOptions({
  providerConfigKeys,
  localDeviceModels,
  connectDevicesLoaded,
}) {
  return (
    <>
      {providerConfigKeys.map(({ key, label }) => {
        const offline = providerBase(key) === 'local' &&
          connectDevicesLoaded &&
          (localDeviceModels[key] ?? []).length === 0;
        return (
          <option key={key} value={key} disabled={offline}>
            {label}{offline ? ' (offline)' : ''}
          </option>
        );
      })}
    </>
  );
}

/**
 * Provider <select> for the Main and Summarizer model sections: a leading
 * placeholder option, a "Default" option, then the user's saved provider
 * configs (offline-aware, via ProviderConfigOptions).  The per-agent
 * dropdown is intentionally not built on this — it has a different option
 * set (Stackform default, no "Default") and a grid layout.
 */
function ProviderSelect({
  value,
  onChange,
  placeholderLabel,
  grayPlaceholder = false,
  providerConfigKeys,
  localDeviceModels,
  connectDevicesLoaded,
  className,
}) {
  return (
    <select value={value} onChange={onChange} className={className}>
      <option value="" style={grayPlaceholder ? PLACEHOLDER_STYLE : undefined}>
        {placeholderLabel}
      </option>
      <option value="default">Default</option>
      <ProviderConfigOptions
        providerConfigKeys={providerConfigKeys}
        localDeviceModels={localDeviceModels}
        connectDevicesLoaded={connectDevicesLoaded}
      />
    </select>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ModelDefaultsTab = ({
  orchestratorProvider,
  handleOrchestratorProviderChange,
  orchestratorModel,
  setOrchestratorModel,
  summarizerProvider,
  handleSummarizerProviderChange,
  summarizerModel,
  setSummarizerModel,
  addModelProvider,
  setAddModelProvider,
  addModelName,
  setAddModelName,
  isAddingModel,
  handleAddModel,
  handleDeleteModel,
  availableModels,
  modelCatalog,
  modelRecommended,
  providerConfigKeys,
  providerConfigs = {},
  agentModelAssignments,
  setAgentModelAssignments,
  personas,
  connectDevices = [],
  connectDevicesLoaded = false,
  profiles = [],
  activeProfileName = 'Default',
  switchProfile,
  createProfile,
  renameProfile,
  deleteProfile,
  isModelDirty = false,
}) => {
  // Each local key's installed models, from the user's Connect devices.
  const localDeviceModels = useMemo(
    () => deviceModelsByKey(providerConfigs, connectDevices),
    [providerConfigs, connectDevices],
  );

  // Append "(recommended)" to the model the DB has flagged as cheapest.
  const modelLabel = (provider, model) => {
    if (modelRecommended && modelRecommended[provider] === model) {
      return `${model} (recommended)`;
    }
    return model;
  };

  // Agent-level add-model form state (one open at a time).
  const [agentAddFormOpen, setAgentAddFormOpen] = useState(null);
  const [agentAddModelName, setAgentAddModelName] = useState('');
  const [isAddingAgentModel, setIsAddingAgentModel] = useState(false);

  const openAgentAddForm = (agentName) => {
    setAgentAddFormOpen(agentName);
    setAgentAddModelName('');
  };
  const closeAgentAddForm = () => {
    setAgentAddFormOpen(null);
    setAgentAddModelName('');
  };

  const submitAgentAddModel = async (agentName, baseProvider) => {
    if (!agentAddModelName.trim()) return;
    setIsAddingAgentModel(true);
    try {
      await handleAddModel(baseProvider, agentAddModelName.trim(), (newModel) => {
        setAgentModelAssignments(prev => ({
          ...prev,
          [agentName]: { ...prev[agentName], model_name: newModel },
        }));
        closeAgentAddForm();
      });
    } finally {
      setIsAddingAgentModel(false);
    }
  };

  /**
   * Build the merged model list for a non-local provider's dropdown.
   * Catalog models come first; the user's custom models for the
   * provider's family (shared by all its keys) after.
   */
  const mergeModels = (providerKey) =>
    modelListFor(providerBase(providerKey), {
      catalog: modelCatalog,
      availableModels,
    });

  /**
   * Render the model selector for a given provider key.
   * Local providers use a device-sourced dropdown (no free-text entry).
   * Other providers use the catalog + custom model flow.
   */
  const renderModelSelect = ({
    providerKey,
    value,
    onChange,
    showAddNew,
    onAddNew,
  }) => {
    const base = providerBase(providerKey);

    if (base === 'local') {
      const deviceModels = localDeviceModels[providerKey] ?? [];
      if (!deviceModels.length) {
        if (!connectDevicesLoaded) {
          return (
            <select disabled className={styles.input}>
              <option>Loading models…</option>
            </select>
          );
        }
        return <OfflineDeviceSelect />;
      }
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={styles.input}
        >
          <option value="" style={PLACEHOLDER_STYLE}>Select a model</option>
          {deviceModels.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      );
    }

    const { all } = mergeModels(providerKey);
    // A saved choice no longer listed (e.g. a removed custom model) stays
    // visible; "__ADD_NEW__" is the add-model option, not a model.
    const unlisted = value === '__ADD_NEW__'
      ? null
      : unlistedOption(all, value, { catalogLoaded: modelCatalog !== null });
    return (
      <select
        value={value}
        onChange={(e) => {
          if (e.target.value === '__ADD_NEW__') {
            onAddNew?.();
          } else {
            onChange(e.target.value);
          }
        }}
        className={styles.input}
      >
        <option value="" style={PLACEHOLDER_STYLE}>
          {modelCatalog === null && base !== 'default'
            ? 'Loading models…'
            : 'Select model'}
        </option>
        {all.map(m => (
          <option key={m} value={m}>{modelLabel(base, m)}</option>
        ))}
        {unlisted && (
          <option value={unlisted.value}>{unlisted.label}</option>
        )}
        {showAddNew && (
          <option value="__ADD_NEW__" style={{ fontWeight: 'bold', color: '#2563eb' }}>
            + Add Custom Model
          </option>
        )}
      </select>
    );
  };

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* Model profile — which profile these settings apply to.             */}
      {/* ------------------------------------------------------------------ */}
      {switchProfile && (
        <div className={styles.section}>
          <ProfileSwitcher
            profiles={profiles}
            activeProfileName={activeProfileName}
            onSwitch={switchProfile}
            onCreate={createProfile}
            onRename={renameProfile}
            onDelete={deleteProfile}
            isDirty={isModelDirty}
            manage
          />
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Agent mode status (circuit breaker) — renders only when a model    */}
      {/* is using simplified routing.                                        */}
      {/* ------------------------------------------------------------------ */}
      <ModelReliabilityPanel />

      {/* ------------------------------------------------------------------ */}
      {/* Set Active Model (orchestrator)                                     */}
      {/* ------------------------------------------------------------------ */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Main Model</h2>
        <p className={styles.sectionDescription}>
          Manage and configure your orchestrator and Swarm main models. Default
          suggested models are always available in the dropdown; use
          "+ Add Custom Model" to add models not in the default list.
        </p>

        <h3 style={{
          fontSize: '1.1rem', fontWeight: '600',
          marginTop: '1rem', marginBottom: '0.75rem',
          color: 'var(--text-color)',
        }}>
          Set Active Model
        </h3>

        <div className={styles.formField}>
          <label className={styles.label}>Provider</label>
          <ProviderSelect
            value={orchestratorProvider}
            onChange={handleOrchestratorProviderChange}
            placeholderLabel="Select Provider"
            grayPlaceholder
            providerConfigKeys={providerConfigKeys}
            localDeviceModels={localDeviceModels}
            connectDevicesLoaded={connectDevicesLoaded}
            className={styles.input}
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.label}>Model Name</label>
          {!orchestratorProvider ? (
            <select disabled className={styles.input}>
              <option>Select provider first</option>
            </select>
          ) : orchestratorProvider === 'default' ? (
            <select
              value={orchestratorModel}
              onChange={(e) => setOrchestratorModel(e.target.value)}
              className={styles.input}
            >
              {(MODEL_OPTIONS.default || []).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            renderModelSelect({
              providerKey: orchestratorProvider,
              value: orchestratorModel,
              onChange: setOrchestratorModel,
              showAddNew: true,
              onAddNew: () => {
                setOrchestratorModel('');
                setAddModelProvider(orchestratorProvider);
                setAddModelName('');
              },
            })
          )}
        </div>

        {/* Add-model form for non-local providers */}
        {orchestratorProvider && orchestratorProvider !== 'default' &&
          providerBase(orchestratorProvider) !== 'local' &&
          addModelProvider === orchestratorProvider && (
          <AddModelForm
            title={`Add custom model for ${
              providerConfigKeys.find(e => e.key === addModelProvider)?.label
              || providerBase(addModelProvider)
            }`}
            value={addModelName}
            onChange={setAddModelName}
            onAdd={handleAddModel}
            onCancel={() => { setAddModelProvider(''); setAddModelName(''); }}
            isAdding={isAddingModel}
          />
        )}

        {/* Custom model chips — non-local providers only */}
        {orchestratorProvider && orchestratorProvider !== 'default' &&
          providerBase(orchestratorProvider) !== 'local' && (() => {
          const { custom } = mergeModels(orchestratorProvider);
          if (!custom.length) return null;
          const base = providerBase(orchestratorProvider);
          return (
            <div style={{ marginTop: '0.75rem' }}>
              <p style={{
                fontSize: '0.8rem',
                color: 'var(--secondary-text-color)',
                marginBottom: '0.35rem',
              }}>
                Custom models for {base}:
              </p>
              <CustomModelChips
                models={custom}
                onRemove={(m) => void handleDeleteModel(base, m)}
              />
            </div>
          );
        })()}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Summarizer Model (workflow synthesis) — optional override          */}
      {/* ------------------------------------------------------------------ */}
      <div className={styles.section} style={{
        borderTop: '2px solid var(--input-border)',
        paddingTop: '1.5rem',
        marginTop: '1.5rem',
      }}>
        <h2 className={styles.sectionTitle}>Summarizer Model</h2>
        <p className={styles.sectionDescription}>
          The model used to synthesize agent reports during workflow runs.
          Leave as "Use main model" to reuse your main model, or pick a
          different provider and model just for summarization.
        </p>

        <div className={styles.formField}>
          <label className={styles.label}>Provider</label>
          <ProviderSelect
            value={summarizerProvider}
            onChange={handleSummarizerProviderChange}
            placeholderLabel="Use main model"
            providerConfigKeys={providerConfigKeys}
            localDeviceModels={localDeviceModels}
            connectDevicesLoaded={connectDevicesLoaded}
            className={styles.input}
          />
        </div>

        {summarizerProvider && (
          <div className={styles.formField}>
            <label className={styles.label}>Model Name</label>
            {summarizerProvider === 'default' ? (
              <select
                value={summarizerModel}
                onChange={(e) => setSummarizerModel(e.target.value)}
                className={styles.input}
              >
                {(MODEL_OPTIONS.default || []).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              renderModelSelect({
                providerKey: summarizerProvider,
                value: summarizerModel,
                onChange: setSummarizerModel,
                showAddNew: false,
              })
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Agent Models                                                        */}
      {/* ------------------------------------------------------------------ */}
      <div className={styles.section} style={{
        borderTop: '2px solid var(--input-border)',
        paddingTop: '1.5rem',
        marginTop: '1.5rem',
      }}>
        <h2 className={styles.sectionTitle}>Agent Models</h2>
        <p className={styles.sectionDescription}>
          Configure each agent's provider and model. Default suggested models
          are always available in the dropdown; use "+ Add Custom Model" to add
          models not in the default list.
        </p>
        <p
          className={styles.sectionDescription}
          style={{
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid var(--input-border)',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            fontSize: '0.85rem',
          }}
        >
          💡 Using fewer agents can speed up responses. Set an agent's provider
          to <strong>None</strong> to disable it — each disabled agent is one
          less model call running in parallel, so you can run just one or two
          instead of all three.
        </p>

        {(() => {
          const DEFAULT_AGENT_NAMES = ['aeri', 'capo', 'cardian'];
          const allEntries = Object.entries(agentModelAssignments);
          const defaultEntries = allEntries
            .filter(([n]) => DEFAULT_AGENT_NAMES.includes(n))
            .sort(([a], [b]) => a.localeCompare(b));
          const customEntries = allEntries
            .filter(([n]) => !DEFAULT_AGENT_NAMES.includes(n))
            .sort(([a], [b]) => a.localeCompare(b));

          const renderAgent = ([agentName, assignment]) => {
            const isDefaultAgent = DEFAULT_AGENT_NAMES.includes(agentName);
            const providerKey = assignment?.provider || '';
            const base = providerBase(providerKey);
            const currentModel = assignment?.model_name || '';
            const isFormOpen = agentAddFormOpen === agentName;
            const { custom: customModels } = providerKey && providerKey !== 'stackform'
              && base !== 'local'
              ? mergeModels(providerKey)
              : { custom: [] };

            return (
              <div className={styles.formField} key={agentName} style={{ marginBottom: '1.25rem' }}>

                {/* Agent header */}
                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', marginBottom: '0.5rem',
                }}>
                  <label className={styles.label} style={{ marginBottom: 0 }}>
                    {agentName.charAt(0).toUpperCase() + agentName.slice(1)}
                    {isDefaultAgent && (
                      <span style={{ fontSize: '0.8rem', color: '#999', marginLeft: '0.5rem' }}>
                        (default)
                      </span>
                    )}
                  </label>
                  {!isDefaultAgent && (
                    <button
                      onClick={() => {
                        const next = { ...agentModelAssignments };
                        delete next[agentName];
                        setAgentModelAssignments(next);
                        if (agentAddFormOpen === agentName) closeAgentAddForm();
                      }}
                      style={{
                        background: 'none', border: 'none', color: '#e5393f',
                        cursor: 'pointer', padding: '0.25rem 0.5rem',
                        fontSize: '0.9rem', fontWeight: '500',
                      }}
                      title={`Remove ${agentName}`}
                    >
                      Remove
                    </button>
                  )}
                </div>

                {/* Provider + model row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.5rem' }}>
                  <select
                    value={assignment?.provider || (isDefaultAgent ? 'stackform' : '')}
                    onChange={(e) => {
                      const newKey = e.target.value;
                      const newBase = newKey.split('_')[0];
                      // Disabled agents carry no model; Stackform uses the
                      // platform default; any other provider auto-selects its
                      // recommended model (matching the left-panel selector)
                      // so the field isn't left on "Select model".
                      const defaultModel = newKey === DISABLED_PROVIDER
                        ? ''
                        : newKey === 'stackform'
                          ? 'default-rapid-model'
                          : pickDefaultModel(newKey, newBase, {
                              catalog: modelCatalog,
                              availableModels,
                              localProviderModels: localDeviceModels,
                              modelRecommended,
                            });
                      closeAgentAddForm();
                      setAgentModelAssignments(prev => ({
                        ...prev,
                        [agentName]: {
                          provider: newKey,
                          model_name: defaultModel,
                          persona_id: prev[agentName]?.persona_id || '',
                        },
                      }));
                    }}
                    className={styles.input}
                  >
                    {!isDefaultAgent && (
                      <option value="" style={PLACEHOLDER_STYLE}>Select provider</option>
                    )}
                    {isDefaultAgent && (
                      <option value={DISABLED_PROVIDER}>None (disabled)</option>
                    )}
                    {isDefaultAgent && (
                      <option value="stackform">Stackform (default)</option>
                    )}
                    <ProviderConfigOptions
                      providerConfigKeys={providerConfigKeys}
                      localDeviceModels={localDeviceModels}
                      connectDevicesLoaded={connectDevicesLoaded}
                    />
                  </select>

                  {/* Model selector */}
                  {providerKey === DISABLED_PROVIDER ? (
                    <select value="" className={styles.input} disabled>
                      <option value="">Agent disabled</option>
                    </select>
                  ) : !providerKey ? (
                    <select value="" className={styles.input} disabled>
                      <option value="" style={PLACEHOLDER_STYLE}>Select model</option>
                    </select>
                  ) : providerKey === 'stackform' ? (
                    <select
                      value={currentModel || 'default-rapid-model'}
                      onChange={(e) => setAgentModelAssignments(prev => ({
                        ...prev,
                        [agentName]: { ...prev[agentName], model_name: e.target.value },
                      }))}
                      className={styles.input}
                    >
                      <option value="default-rapid-model">default-rapid-model</option>
                    </select>
                  ) : (
                    renderModelSelect({
                      providerKey,
                      value: currentModel,
                      onChange: (val) => setAgentModelAssignments(prev => ({
                        ...prev,
                        [agentName]: { ...prev[agentName], model_name: val },
                      })),
                      showAddNew: base !== 'local',
                      onAddNew: () => openAgentAddForm(agentName),
                    })
                  )}
                </div>

                {/* Custom model chips — non-local providers only */}
                {providerKey && providerKey !== 'stackform' &&
                  base !== 'local' && customModels.length > 0 && (
                  <CustomModelChips
                    models={customModels}
                    onRemove={(m) => void handleDeleteModel(base, m)}
                  />
                )}

                {/* Inline add-model form — non-local providers only */}
                {isFormOpen && base !== 'local' && (
                  <AddModelForm
                    title={`Add custom model for ${base}`}
                    value={agentAddModelName}
                    onChange={setAgentAddModelName}
                    onAdd={() => void submitAgentAddModel(agentName, base)}
                    onCancel={closeAgentAddForm}
                    isAdding={isAddingAgentModel}
                  />
                )}

                {/* Persona */}
                <div style={{ marginTop: '0.5rem' }}>
                  <label className={styles.label} style={{ fontSize: '0.8rem' }}>Persona</label>
                  <select
                    value={assignment?.persona_id || ''}
                    disabled={providerKey === DISABLED_PROVIDER}
                    onChange={(e) => setAgentModelAssignments(prev => ({
                      ...prev,
                      [agentName]: { ...prev[agentName], persona_id: e.target.value },
                    }))}
                    className={styles.input}
                  >
                    <option value="">None</option>
                    {personas
                      .filter(p => p.id && (p.title || '').trim() && (p.prompt || '').trim())
                      .map(p => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                  </select>
                </div>
              </div>
            );
          };

          return (
            <>
              {defaultEntries.map(renderAgent)}
              {customEntries.length > 0 && (
                <>
                  <hr style={{
                    border: 'none',
                    borderTop: '1px solid var(--border-color, #e0e0e0)',
                    margin: '1rem 0',
                  }} />
                  <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '0.75rem', marginTop: 0 }}>
                    Custom sub-agents
                  </p>
                  {customEntries.map(renderAgent)}
                </>
              )}
            </>
          );
        })()}

        {/* Add New Sub-Agent */}
        <div style={{ marginTop: '1rem' }}>
          <button
            onClick={() => {
              const customAgents = Object.keys(agentModelAssignments).filter(
                n => !['aeri', 'capo', 'cardian'].includes(n)
              );
              if (customAgents.length >= 2) {
                alert('Maximum 2 custom sub-agents allowed. Remove one to add another.');
                return;
              }
              const name = prompt('Enter new sub-agent name (e.g., andes, olympus):');
              if (!name || !name.trim()) return;
              const key = name.trim().toLowerCase();
              if (!/^[a-z][a-z0-9-]{0,29}$/.test(key)) {
                alert('Agent name must start with a letter and contain only lowercase letters, numbers, or hyphens (max 30 characters).');
                return;
              }
              if (agentModelAssignments[key]) {
                alert('An agent with this name already exists.');
                return;
              }
              const firstEntry = providerConfigKeys[0] || null;
              const firstKey = firstEntry ? firstEntry.key : '';
              const firstModel = firstKey
                ? (mergeModels(firstKey).all[0] || '')
                : '';
              setAgentModelAssignments(prev => ({
                ...prev,
                [key]: { provider: firstKey, model_name: firstModel, persona_id: '' },
              }));
            }}
            className={styles.button}
            style={{
              backgroundColor: 'var(--button-background)',
              color: 'var(--button-text)',
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              minWidth: 'auto',
            }}
          >
            + Add New Sub-Agent
          </button>
        </div>
      </div>
    </>
  );
};

export default ModelDefaultsTab;
